import { React, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { COLORS } from "../../utils/colors";
import { api } from "../../utils/api";
import PendingVehicleActivationModal from "../../components/PendingVehicleActivationModal";
import { showErrorToast } from "../../utils/toast";

const VehicleList = ({ vehicles, onVehiclesChanged }) => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(window.innerWidth <= 500);
  const [inviteCodes, setInviteCodes] = useState({});
  const [activationItems, setActivationItems] = useState([]);
  const [activationMode, setActivationMode] = useState("activation");
  const [showActivationModal, setShowActivationModal] = useState(false);

  const handleWindowSizeChange = () => {
    setMobile(window.innerWidth <= 500);
  };

  const fetchInviteCode = async (vehicleId, customerId) => {
    try {
      const getInviteCode = await api("v1/inviteCode/search", "POST", {
        vehicle: vehicleId,
        customer: customerId,
      });
      if (
        getInviteCode.data?.success &&
        Array.isArray(getInviteCode.data.data?.searchResult) &&
        getInviteCode.data.data.searchResult.length > 0
      ) {
        return getInviteCode.data.data.searchResult[0].code;
      }
    } catch (error) {
      console.error("Error fetching invite code:", error);
    }
    return null;
  };

  const openPendingVehicleModal = async () => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
      if (currentUser?.email) {
        sessionStorage.setItem("authEmail", String(currentUser.email).toLowerCase().trim());
      }

      const captureRes = await api("v1/customer/me/pending-vehicle-details", "GET");
      if (captureRes?.status === 401) {
        showErrorToast("Session expired. Please log out and log in again.");
        return false;
      }
      if (
        captureRes.data?.success &&
        Array.isArray(captureRes.data.data?.items) &&
        captureRes.data.data.items.length > 0
      ) {
        setActivationMode("capture");
        setActivationItems(captureRes.data.data.items);
        setShowActivationModal(true);
        return true;
      }

      const res = await api("v1/customer/me/pending-vehicle-activations", "GET");
      if (!res || res.status === 401) {
        showErrorToast("Session expired. Please log out and log in again.");
        return false;
      }
      if (
        res.data?.success &&
        Array.isArray(res.data.data?.items) &&
        res.data.data.items.length > 0
      ) {
        setActivationMode("activation");
        setActivationItems(res.data.data.items);
        setShowActivationModal(true);
        return true;
      }
      if (res.data?.success) {
        showErrorToast("No vehicles are waiting for activation.");
      } else {
        showErrorToast(res.data?.message || "Could not load vehicle activation.");
      }
    } catch (error) {
      console.error("Error loading pending vehicle flows:", error);
      showErrorToast("Could not load vehicle activation. Try again or contact support.");
      return false;
    }
    return false;
  };

  const handleRowClick = async (vehicle, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (vehicle.active) {
      navigate(`/vehicle/${vehicle._id}`);
      return;
    }

    const portal = sessionStorage.getItem("selectedPortal");
    if (portal !== "Customer") {
      navigate(`/vehicle/${vehicle._id}`);
      return;
    }

    const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");

    // Customer portal users passed initialFetch as active customers — always use popup, never invite codes
    if (currentUser._id) {
      await openPendingVehicleModal();
      return;
    }

    // Fallback: brand-new account activation via invite + OTP (should not reach here in Customer portal)
    let inviteCode = inviteCodes[vehicle._id];
    const customerId = vehicle.customer?._id || vehicle.customer;
    if (!inviteCode && customerId) {
      inviteCode = await fetchInviteCode(vehicle._id, customerId);
      if (inviteCode) {
        setInviteCodes((prevCodes) => ({ ...prevCodes, [vehicle._id]: inviteCode }));
      }
    }
    if (!inviteCode) {
      showErrorToast("No activation code found. Please contact support.");
      return;
    }
    window.location.href = `/activate?code=${inviteCode}`;
  };

  const handleActivationResolved = async ({ mode } = {}) => {
    try {
      const captureRes = await api("v1/customer/me/pending-vehicle-details", "GET");
      if (
        captureRes.data?.success &&
        Array.isArray(captureRes.data.data?.items) &&
        captureRes.data.data.items.length > 0
      ) {
        setActivationMode("capture");
        setActivationItems(captureRes.data.data.items);
        return;
      }

      const res = await api("v1/customer/me/pending-vehicle-activations", "GET");
      if (res.data?.success && Array.isArray(res.data.data?.items) && res.data.data.items.length > 0) {
        setActivationMode("activation");
        setActivationItems(res.data.data.items);
      } else {
        setActivationItems([]);
        setShowActivationModal(false);
        if (onVehiclesChanged) {
          onVehiclesChanged();
        }
      }
      if (mode === "activation") {
        const userInfo = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
        if (userInfo._id) {
          await api(`v1/contract/check-and-create/${userInfo._id}`, "POST");
        }
      }
    } catch (e) {
      setActivationItems([]);
      setShowActivationModal(false);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const isCustomerPortal = sessionStorage.getItem("selectedPortal") === "Customer";
  const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
  const inactiveLabel =
    isCustomerPortal && currentUser.active
      ? "Finish Setup to Activate"
      : isCustomerPortal
        ? "Activate Account"
        : "View Vehicle";

  return (
    <>
      {vehicles.length > 0 && mobile ? (
        <div className="flex flex-col justify-center">
          {vehicles.map((vehicle) => (
            <VehicleCard
              key={vehicle._id}
              vehicle={vehicle}
              inactiveLabel={inactiveLabel}
              onActivate={() => handleRowClick(vehicle)}
              onView={() => navigate(`/vehicle/${vehicle._id}`)}
            />
          ))}
        </div>
      ) : (
        <TableContainer
          sx={{
            width: "fit-content",
            maxWidth: "calc(100vw - 200px)",
            margin: "0 auto",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
              "&:hover": {
                background: "#555",
              },
            },
          }}
        >
          <Table
            sx={{
              minWidth: 650,
              borderCollapse: "separate",
              "td, th": { borderBottom: "none" },
              th: { fontSize: "18px" },
            }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow>
                <TableCell>Rego</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Vin</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((vehicle) => (
                <TableRow
                  key={vehicle._id}
                  sx={{
                    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
                    "&:hover": { cursor: "pointer" },
                  }}
                >
                  <TableCell scope="row">{vehicle.rego}</TableCell>
                  <TableCell>
                    {vehicle.customer && vehicle.customer.address
                      ? vehicle.customer.address.state
                      : "N/A"}
                  </TableCell>
                  <TableCell>{vehicle.vin}</TableCell>
                  <TableCell>
                    {vehicle.customer
                      ? `${vehicle.customer.firstName} ${vehicle.customer.lastName}`
                      : "N/A"}
                  </TableCell>
                  <TableCell>{vehicle.active ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: vehicle.active ? COLORS.priorityBlue.hex : "red",
                      }}
                      onClick={(e) => handleRowClick(vehicle, e)}
                    >
                      {vehicle.active ? "View Vehicle" : inactiveLabel}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showActivationModal && activationItems.length > 0 && (
        <PendingVehicleActivationModal
          key={`${activationMode}-${activationItems[0].vehicleId}`}
          mode={activationMode}
          items={activationItems}
          onResolved={handleActivationResolved}
        />
      )}
    </>
  );
};

const VehicleCard = ({ vehicle, inactiveLabel, onActivate, onView }) => (
  <div className="flex flex-col border border-1 shadow-sm w-[300px] p-4 mb-4">
    <div className="grid grid-cols-2 mb-4">
      <span className="font-semibold">Rego</span>
      <span>{vehicle.rego}</span>
      <span className="font-semibold">Vin</span>
      <span>{vehicle.vin}</span>
      <span className="font-semibold">Customer</span>
      <span>
        {vehicle.customer
          ? `${vehicle.customer.firstName} ${vehicle.customer.lastName}`
          : "N/A"}
      </span>
      <span className="font-semibold">Active</span>
      <span>{vehicle.active ? "YES" : "NO"}</span>
    </div>
    <Button
      onClick={vehicle.active ? onView : onActivate}
      sx={{ backgroundColor: vehicle.active ? COLORS.priorityBlue.hex : "red" }}
      variant="contained"
    >
      {vehicle.active ? "View Vehicle" : inactiveLabel}
    </Button>
  </div>
);

export default VehicleList;
